import type { InsurancePolicyModel } from "@/api/cle-api";
import EditIcon from "@/components/icons/EditIcon";
import { ModalProvider, ModalTrigger } from "@/components/Modal/Modal";
import { UserQueryContext } from "@/context/UserContext";
import { useContext } from "react";

import { ValidationFieldItem } from "../person/id/Efteruddannelse/ValidationFieldItem";
import {
  formatAmount,
  formatDateLong,
} from "../person/id/Efteruddannelse/utils/formatters";
import { InsurancePolicyValidationEditModal } from "./InsurancePolicyValidationEditModal";
import {
  getInsurancePolicyUnmatchedFailingFields,
  getInsurancePolicyValidationFieldStatus,
} from "./utils/insurancePolicyValidationFieldStatus";

interface InsurancePolicyValidationProps {
  policy: InsurancePolicyModel;
  onPolicyUpdated?: (updated: InsurancePolicyModel) => void;
}

export const InsurancePolicyValidation = ({
  policy,
  onPolicyUpdated,
}: InsurancePolicyValidationProps) => {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canEdit = hasPermission(user, "TilsynWrite");
  const fieldStatus = getInsurancePolicyValidationFieldStatus(policy);
  const unmatchedFailingFields =
    getInsurancePolicyUnmatchedFailingFields(policy);

  const handleSave = (updated: InsurancePolicyModel) => {
    onPolicyUpdated?.(updated);
  };

  return (
    <ModalProvider>
      <div className="c-card">
        <div className="flex-100 d-flex justify-content-between align-items-center mb-0">
          <h3 className="mb-0">Validering</h3>
          {canEdit && (
            <ModalTrigger className="btn btn-primary btn--transparent has-icon px-2 edit-trigger">
              Redigér
              <EditIcon height={16} />
            </ModalTrigger>
          )}
        </div>
        <div className="c-item-list">
          <ValidationFieldItem
            isValid={fieldStatus.insuranceCompany.approved}
            label="Forsikringsselskab"
            reason={fieldStatus.insuranceCompany.reason}
            value={policy.insuranceCompany ?? "-"}
          />
          <ValidationFieldItem
            isValid={fieldStatus.policyStartDate.approved}
            label="Policens startdato"
            reason={fieldStatus.policyStartDate.reason}
            value={formatDateLong(policy.policyStartDate)}
          />
          <ValidationFieldItem
            isValid={fieldStatus.lawyerLiabilityAmount.approved}
            label="Advokatansvar (beløb)"
            reason={fieldStatus.lawyerLiabilityAmount.reason}
            value={formatAmount(policy.lawyerLiabilityAmount)}
          />
          <ValidationFieldItem
            isValid={fieldStatus.guaranteeAmount.approved}
            label="Garantibeløb"
            reason={fieldStatus.guaranteeAmount.reason}
            value={formatAmount(policy.guaranteeAmount)}
          />
          {/* See the note in CourseCertificateValidation.tsx. */}
          {unmatchedFailingFields.map((field) => (
            <ValidationFieldItem
              key={field.key}
              isValid={false}
              label={field.label}
              reason={field.reason}
              value="-"
            />
          ))}
        </div>
      </div>
      {canEdit && (
        <InsurancePolicyValidationEditModal
          policy={policy}
          onSave={handleSave}
        />
      )}
    </ModalProvider>
  );
};
